import {
  AlertTriangle,
  CalendarRange,
  Download,
  FileDown,
  ImageDown,
  LoaderCircle,
  MessageSquareText,
  PieChart,
  RefreshCw,
  Table2,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { ReportDataWarnings } from '../../components/ReportDataWarnings';
import { requireFirebaseFunctions } from '../../lib/firebase';
import { formatReportGeneration, OUTPUT_REPORT_CALLABLE_OPTIONS } from '../../lib/generated-report';
import {
  outputReportMonths,
  outputReportYears,
  type ProcessingAvailability,
} from '../../lib/processing-data';
import {
  createSocialReasonEntryWorkbook,
  socialReasonEntryWorkbookFileName,
  type SocialReasonEntryAnalysis,
  type SocialReasonOperationalRowKey,
} from '../../lib/social-reason-entry';
import { svgElementMarkupForExport, svgMarkupDataUrl } from '../../lib/svg-export';
import './SocialReasonEntryPanel.css';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];
const ROW_COLORS: Record<SocialReasonOperationalRowKey, string> = {
  incoming: '#e8b1ea',
  closed: '#ef2f73',
  'agency-backlog': '#fff34f',
  working: '#2f344c',
};
const DAILY_CLOSED_COLOR = '#ef2f73';
const DAILY_WORKING_COLOR = '#2f344c';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

function formatNumber(value: number, maximumFractionDigits = 0): string {
  return new Intl.NumberFormat('it-IT', { maximumFractionDigits }).format(value);
}

function defaultMonthForYear(year: number): number {
  const now = new Date();
  return year === now.getFullYear() ? Math.max(1, now.getMonth()) : 12;
}

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message.replace(/^Firebase:\s*/i, '').replace(/\s*\([^)]*\)\.?$/, '');
  }
  return 'Non è stato possibile generare il report Ingresso Social Reason.';
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function polarPoint(cx: number, cy: number, radius: number, angle: number): { x: number; y: number } {
  const radians = ((angle - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) };
}

function piePath(cx: number, cy: number, radius: number, start: number, end: number): string {
  const startPoint = polarPoint(cx, cy, radius, end);
  const endPoint = polarPoint(cx, cy, radius, start);
  const largeArc = end - start <= 180 ? 0 : 1;
  return `M ${cx} ${cy} L ${startPoint.x} ${startPoint.y} A ${radius} ${radius} 0 ${largeArc} 0 ${endPoint.x} ${endPoint.y} Z`;
}

function niceAxisMaximum(maximum: number): { maximum: number; step: number } {
  if (maximum <= 0) return { maximum: 100, step: 25 };
  const roughStep = maximum / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const step = factor * magnitude;
  return { maximum: step * Math.ceil(maximum / step), step };
}

const OperationalChart = forwardRef<SVGSVGElement, { analysis: SocialReasonEntryAnalysis }>(function OperationalChart({ analysis }, ref) {
  const positiveRows = analysis.operationalRows.filter(({ value }) => value > 0);
  const total = positiveRows.reduce((sum, row) => sum + row.value, 0);
  const segments = positiveRows.map((row, index) => {
    const start = positiveRows.slice(0, index).reduce((sum, previous) => (
      sum + (previous.value / Math.max(total, 1)) * 360
    ), 0);
    const end = start + (row.value / Math.max(total, 1)) * 360;
    return { row, start, end };
  });

  return (
    <svg ref={ref} className="social-reason-pie-svg" viewBox="0 0 960 500" role="img" aria-labelledby="social-reason-operational-title social-reason-operational-description" xmlns="http://www.w3.org/2000/svg">
      <title id="social-reason-operational-title">Stato operativo Social Reason Creation</title>
      <desc id="social-reason-operational-description">Distribuzione mensile tra chiuse, backlog agenzia, in lavorazione e in entrata.</desc>
      <rect width="960" height="500" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="42" y="43" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="18" fontWeight="700">Stato operativo case</text>
      <text data-export-title="true" x="42" y="67" fill="#7b8a96" fontFamily="Inter, Arial, sans-serif" fontSize="12">{analysis.periodLabel} · Social Reason {analysis.socialReason}</text>

      {total === 0 ? (
        <g>
          <circle cx="290" cy="275" r="150" fill="#f2f5f7" stroke="#d9e1e6" strokeWidth="2" />
          <text x="290" y="282" textAnchor="middle" fill="#86949f" fontFamily="Inter, Arial, sans-serif" fontSize="16" fontWeight="700">Nessun caso</text>
        </g>
      ) : segments.length === 1 ? (
        <circle cx="290" cy="275" r="150" fill={ROW_COLORS[segments[0].row.key]}>
          <title>{segments[0].row.label}: {formatNumber(segments[0].row.value)}</title>
        </circle>
      ) : segments.map(({ row, start, end }) => (
        <path key={row.key} d={piePath(290, 275, 150, start, end)} fill={ROW_COLORS[row.key]} stroke="#ffffff" strokeWidth="2">
          <title>{row.label}: {formatNumber(row.value)}</title>
        </path>
      ))}

      <g transform="translate(535 145)">
        {analysis.operationalRows.map((row, index) => {
          const percentage = total > 0 ? (row.value / total) * 100 : 0;
          return (
            <g key={row.key} transform={`translate(0 ${index * 72})`}>
              <rect width="16" height="16" y="2" rx="3" fill={ROW_COLORS[row.key]} stroke={row.key === 'agency-backlog' ? '#d6cb37' : 'none'} />
              <text x="28" y="16" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="14" fontWeight="700">{row.label}</text>
              <text x="28" y="39" fill="#667987" fontFamily="Inter, Arial, sans-serif" fontSize="12">{formatNumber(row.value)} · {formatNumber(percentage, 1)}%</text>
            </g>
          );
        })}
      </g>
    </svg>
  );
});

const DailyChart = forwardRef<SVGSVGElement, { analysis: SocialReasonEntryAnalysis }>(function DailyChart({ analysis }, ref) {
  const axis = niceAxisMaximum(Math.max(0, ...analysis.dailyRows.map(({ totalWorked }) => totalWorked)));
  const plot = { left: 68, top: 44, width: 1140, height: 340 };
  const step = plot.width / Math.max(analysis.dailyRows.length, 1);
  const barWidth = Math.max(6, Math.min(24, step * 0.62));
  const ticks = Array.from({ length: Math.round(axis.maximum / axis.step) + 1 }, (_, index) => index * axis.step);

  return (
    <svg ref={ref} className="social-reason-daily-svg" viewBox="0 0 1280 500" role="img" aria-labelledby="social-reason-daily-title social-reason-daily-description" xmlns="http://www.w3.org/2000/svg">
      <title id="social-reason-daily-title">Andamento giornaliero {analysis.periodLabel}</title>
      <desc id="social-reason-daily-description">Case chiusi e in lavorazione per ogni giorno del mese selezionato.</desc>
      <rect width="1280" height="500" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="68" y="24" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="16" fontWeight="700">Andamento giornaliero</text>
      {ticks.map((tick) => {
        const y = plot.top + plot.height - (tick / axis.maximum) * plot.height;
        return (
          <g key={tick}>
            <line x1={plot.left} x2={plot.left + plot.width} y1={y} y2={y} stroke="#dfe4e7" strokeWidth="1" />
            <text x={plot.left - 12} y={y + 4} textAnchor="end" fill="#5f6f7b" fontFamily="Inter, Arial, sans-serif" fontSize="11">{formatNumber(tick)}</text>
          </g>
        );
      })}
      {analysis.dailyRows.map((row, index) => {
        const x = plot.left + step * index + (step - barWidth) / 2;
        const closedHeight = (row.closed / axis.maximum) * plot.height;
        const workingHeight = (row.working / axis.maximum) * plot.height;
        const closedY = plot.top + plot.height - closedHeight;
        const workingY = closedY - workingHeight;
        return (
          <g key={row.date}>
            <rect x={x} y={closedY} width={barWidth} height={closedHeight} rx="2" fill={DAILY_CLOSED_COLOR}>
              <title>Giorno {row.day}: Chiuse, {formatNumber(row.closed)}</title>
            </rect>
            <rect x={x} y={workingY} width={barWidth} height={workingHeight} rx="2" fill={DAILY_WORKING_COLOR}>
              <title>Giorno {row.day}: In lavorazione, {formatNumber(row.working)}</title>
            </rect>
            <text x={x + barWidth / 2} y={plot.top + plot.height + 20} textAnchor="middle" fill="#526675" fontFamily="Inter, Arial, sans-serif" fontSize="9.5" fontWeight="600">{row.day}</text>
          </g>
        );
      })}
      <line x1={plot.left} x2={plot.left + plot.width} y1={plot.top + plot.height} y2={plot.top + plot.height} stroke="#687782" strokeWidth="1.2" />
      <g transform="translate(448 455)">
        <rect width="14" height="14" rx="3" fill={DAILY_CLOSED_COLOR} />
        <text x="22" y="12" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="12" fontWeight="650">Chiuse</text>
        <rect x="120" width="14" height="14" rx="3" fill={DAILY_WORKING_COLOR} />
        <text x="142" y="12" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="12" fontWeight="650">In lavorazione</text>
        <text x="280" y="12" fill="#667987" fontFamily="Inter, Arial, sans-serif" fontSize="11">Totale lavorate = somma delle due serie</text>
      </g>
    </svg>
  );
});

function demoAnalysis(): SocialReasonEntryAnalysis {
  const closed = [144, 65, 270, 278, 289, 282, 211, 116, 66, 209, 194, 176, 170, 147, 0, 102, 244, 249, 258, 221, 186, 110, 75, 363, 381, 325, 288, 311, 134, 79, 275];
  const working = [14, 8, 25, 32, 24, 29, 29, 15, 11, 32, 35, 28, 34, 28, 0, 20, 21, 44, 36, 31, 12, 12, 13, 31, 26, 40, 20, 31, 22, 11, 48];
  const dailyRows = closed.map((closedValue, index) => {
    const day = index + 1;
    const date = new Date(Date.UTC(2026, 7, day));
    return {
      day,
      date: `2026-08-${String(day).padStart(2, '0')}`,
      weekday: new Intl.DateTimeFormat('it-IT', { weekday: 'long', timeZone: 'UTC' }).format(date),
      working: working[index],
      backlog: 0,
      closed: closedValue,
      totalWorked: working[index] + closedValue,
    };
  });
  const average = (key: 'working' | 'backlog' | 'closed' | 'totalWorked') => dailyRows.reduce((sum, row) => sum + row[key], 0) / dailyRows.length;
  return {
    selectedYear: 2026,
    selectedMonth: 8,
    periodLabel: 'Agosto 2026',
    cutoffDay: 12,
    socialReason: 'Creation',
    operationalRows: [
      { key: 'incoming', label: 'In entrata', value: 0 },
      { key: 'closed', label: 'Chiuse', value: 51 },
      { key: 'agency-backlog', label: 'Backlog agenzia', value: 0 },
      { key: 'working', label: 'In lavorazione', value: 52 },
    ],
    operationalTotals: { incoming: 0, closed: 51, agencyBacklog: 0, working: 52 },
    closedComponents: { masterWeb: 51, mailSocialFirst12: 0 },
    dailyRows,
    dailyAverages: { working: average('working'), backlog: 0, closed: average('closed'), totalWorked: average('totalWorked') },
    masterFile: { id: 'master', name: '2026.xlsx', path: 'Export / Export annuale / 2026.xlsx', modifiedTime: null, webViewLink: null },
    mailFile: { id: 'mail', name: 'Report 21.09.2026.xlsx', path: 'Mail / 2026 / 09. Settembre / Report 21.09.2026.xlsx', modifiedTime: null, webViewLink: null, referenceDate: '2026-09-21' },
    responsesFile: { id: 'responses', name: 'Risposte - [Report CRM - OF] - Casi inviati al reparto', path: 'Google Sheets / Risposte', modifiedTime: null, webViewLink: null, worksheetName: 'Risposte del modulo 1' },
  };
}

export function SocialReasonEntryPanel({ availability, demo }: { availability: ProcessingAvailability | null; demo: boolean }) {
  const operationalSvgRef = useRef<SVGSVGElement>(null);
  const dailySvgRef = useRef<SVGSVGElement>(null);
  const [year, setYear] = useState<number | ''>('');
  const [month, setMonth] = useState<number | ''>('');
  const [analysis, setAnalysis] = useState<SocialReasonEntryAnalysis | null>(null);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const availableYears = outputReportYears(availability, 'social-reason-entry');
  const selectedYear = year;
  const availableMonths = selectedYear
    ? outputReportMonths(availability, 'social-reason-entry', selectedYear)
    : [];
  const selectedMonth = month;

  useEffect(() => {
    if (year || availableYears.length === 0) return;
    const initialYear = availableYears[0];
    const timeout = window.setTimeout(() => {
      setYear(initialYear);
      setMonth(outputReportMonths(availability, 'social-reason-entry', initialYear).at(-1) ?? defaultMonthForYear(initialYear));
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [availability, availableYears, year]);

  const loadAnalysis = useCallback(async (regenerate = false) => {
    if (!selectedYear || !selectedMonth) return;
    setStatus('loading');
    setError(null);
    setExportError(null);
    try {
      const result = demo
        ? {
            ...demoAnalysis(),
            generation: {
              generatedAt: new Date().toISOString(),
              source: regenerate ? 'manual' as const : 'nightly' as const,
              cached: !regenerate,
              version: 3,
            },
          }
        : (await httpsCallable<{ year: number; month: number; regenerate: boolean }, SocialReasonEntryAnalysis>(
            requireFirebaseFunctions(),
            'getOutputSocialReasonEntryReport',
            OUTPUT_REPORT_CALLABLE_OPTIONS,
          )({ year: selectedYear, month: selectedMonth, regenerate })).data;
      setAnalysis(result);
      setStatus('ready');
    } catch (cause) {
      setError(errorMessage(cause));
      setStatus('error');
    }
  }, [demo, selectedMonth, selectedYear]);

  useEffect(() => {
    if (!selectedYear || !selectedMonth || status !== 'idle') return;
    const timeout = window.setTimeout(() => void loadAnalysis(false), 0);
    return () => window.clearTimeout(timeout);
  }, [loadAnalysis, selectedMonth, selectedYear, status]);

  const exportSvg = (svg: SVGSVGElement | null, fileName: string) => {
    if (!svg) return;
    const serialized = svgElementMarkupForExport(svg);
    downloadBlob(new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`], { type: 'image/svg+xml;charset=utf-8' }), `${fileName}.svg`);
  };

  const exportPng = async (svg: SVGSVGElement | null, fileName: string, width: number, height: number) => {
    if (!svg) return;
    setExportError(null);
    try {
      const image = new Image();
      image.decoding = 'async';
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('Immagine non generata'));
        image.src = svgMarkupDataUrl(svgElementMarkupForExport(svg));
      });
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas non disponibile');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);
      const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png', 1));
      if (!png) throw new Error('PNG non generato');
      downloadBlob(png, `${fileName}.png`);
    } catch {
      setExportError('Non è stato possibile esportare il grafico in PNG. Puoi usare il formato SVG.');
    }
  };

  const exportExcel = () => {
    if (!analysis) return;
    setExportError(null);
    try {
      const workbook = createSocialReasonEntryWorkbook(analysis);
      downloadBlob(
        new Blob([Uint8Array.from(workbook).buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        socialReasonEntryWorkbookFileName(analysis.selectedYear, analysis.selectedMonth),
      );
    } catch {
      setExportError('Non è stato possibile creare il file Excel.');
    }
  };

  const fileBase = analysis
    ? `ingresso-social-reason-${analysis.selectedYear}-${String(analysis.selectedMonth).padStart(2, '0')}`
    : 'ingresso-social-reason';

  return (
    <>
      <section className="trend-period-card">
        <div className="trend-period-copy">
          <span><CalendarRange size={22} /></span>
          <div>
            <p>Periodo di analisi</p>
            <h2>Seleziona anno e mese</h2>
            <small>Il report combina Social Reason Creation, ultimo Mail annuale e risposte giornaliere del team.</small>
          </div>
        </div>
        <div className="trend-period-actions">
          <label>
            <span>Anno</span>
            <select value={selectedYear} disabled={!availability || status === 'loading'} onChange={(event) => {
              const nextYear = Number(event.target.value);
              setYear(nextYear);
              setMonth(outputReportMonths(availability, 'social-reason-entry', nextYear).at(-1) ?? defaultMonthForYear(nextYear));
              setAnalysis(null);
              setStatus('idle');
            }}>
              {!availableYears.length && <option value="">Caricamento…</option>}
              {availableYears.map((availableYear) => <option value={availableYear} key={availableYear}>{availableYear}</option>)}
            </select>
          </label>
          <label>
            <span>Mese</span>
            <select value={selectedMonth} disabled={!selectedYear || status === 'loading'} onChange={(event) => {
              setMonth(Number(event.target.value));
              setAnalysis(null);
              setStatus('idle');
            }}>
              {!selectedMonth && <option value="">Seleziona</option>}
              {availableMonths.map((availableMonth) => (
                <option value={availableMonth} key={availableMonth}>{MONTHS[availableMonth - 1]}</option>
              ))}
            </select>
          </label>
          <button className="button button-primary" type="button" onClick={() => void loadAnalysis(analysis !== null)} disabled={!selectedYear || !selectedMonth || status === 'loading'}>
            {status === 'loading' ? <LoaderCircle className="spin" size={17} /> : <MessageSquareText size={17} />}
            {status === 'loading' ? 'Elaborazione…' : analysis ? 'Rigenera report' : 'Genera report'}
          </button>
        </div>
      </section>

      {status === 'idle' && (
        <section className="trend-empty-state">
          <span><MessageSquareText size={24} /></span>
          <div><strong>Il report è pronto per essere generato</strong><p>Scegli il periodo per calcolare stato operativo e andamento giornaliero.</p></div>
        </section>
      )}
      {status === 'loading' && (
        <section className="trend-empty-state loading">
          <span><LoaderCircle className="spin" size={24} /></span>
          <div><strong>Sto combinando Master, Mail e Risposte</strong><p>Calcolo Social Reason Creation e valori giornalieri del periodo.</p></div>
        </section>
      )}
      {status === 'error' && (
        <section className="trend-empty-state error">
          <span><AlertTriangle size={24} /></span>
          <div><strong>Report non disponibile</strong><p>{error}</p></div>
          <button type="button" className="button button-secondary" onClick={() => void loadAnalysis(false)}><RefreshCw size={16} /> Riprova</button>
        </section>
      )}

      {status === 'ready' && analysis && (
        <>
          <section className="trend-summary-grid" aria-label="Sintesi Ingresso Social Reason">
            <article><span>Social Reason</span><strong>{analysis.socialReason}</strong><small>filtro applicato al Master</small></article>
            <article><span>Chiuse</span><strong>{formatNumber(analysis.operationalTotals.closed)}</strong><small>Web + Social fino al giorno {analysis.cutoffDay}</small></article>
            <article><span>In lavorazione</span><strong>{formatNumber(analysis.operationalTotals.working)}</strong><small>esclusa la Coda SN - Social Cases</small></article>
            <article><span>Backlog agenzia</span><strong>{formatNumber(analysis.operationalTotals.agencyBacklog)}</strong><small>assegnate alla Coda SN - Social Cases</small></article>
          </section>

          {analysis.generation && (
            <div className="report-generation-note" role="status">
              <RefreshCw size={15} />
              <span>{formatReportGeneration(analysis.generation)}. Il pulsante “Rigenera report” sovrascrive questa versione.</span>
            </div>
          )}

          <ReportDataWarnings warnings={analysis.dataWarnings} />

          <section className="social-reason-source-note">
            <strong>Fonti</strong>
            <span>{analysis.masterFile.name}</span>
            <span>{analysis.mailFile.name}</span>
            <span>{analysis.responsesFile.name}</span>
            <button type="button" className="button button-primary" onClick={exportExcel}><Download size={15} /> Esporta Excel</button>
          </section>

          <section className="social-reason-report-grid">
            <article className="trend-chart-card social-reason-chart-card">
              <header>
                <div><p>Grafico 1</p><h2>Stato operativo case</h2><span>Distribuzione mensile della Social Reason Creation.</span></div>
                <div className="trend-export-actions">
                  <button type="button" className="button button-secondary" onClick={() => void exportPng(operationalSvgRef.current, `${fileBase}-stato-operativo`, 1920, 1000)}><ImageDown size={15} /> PNG</button>
                  <button type="button" className="button button-secondary" onClick={() => exportSvg(operationalSvgRef.current, `${fileBase}-stato-operativo`)}><FileDown size={15} /> SVG</button>
                </div>
              </header>
              <div className="social-reason-chart-wrap"><OperationalChart analysis={analysis} ref={operationalSvgRef} /></div>
            </article>

            <article className="trend-table-card social-reason-operational-table-card">
              <header><div><p>Tabella 1</p><h2>Mensile</h2><span>Valori usati nel grafico.</span></div><PieChart size={21} /></header>
              <div className="trend-table-scroll">
                <table className="trend-table social-reason-operational-table">
                  <thead><tr><th>Mensile</th><th>Stato operativo case</th></tr></thead>
                  <tbody>{analysis.operationalRows.map((row) => <tr key={row.key}><th scope="row">{row.label}</th><td>{formatNumber(row.value)}</td></tr>)}</tbody>
                </table>
              </div>
            </article>
          </section>

          <section className="trend-chart-card social-reason-daily-card">
            <header>
              <div><p>Grafico 2</p><h2>Andamento giornaliero</h2><span>Totale lavorate come somma di chiuse e risposte inviate al reparto.</span></div>
              <div className="trend-export-actions">
                <button type="button" className="button button-secondary" onClick={() => void exportPng(dailySvgRef.current, `${fileBase}-andamento-giornaliero`, 2560, 1000)}><ImageDown size={15} /> PNG</button>
                <button type="button" className="button button-secondary" onClick={() => exportSvg(dailySvgRef.current, `${fileBase}-andamento-giornaliero`)}><FileDown size={15} /> SVG</button>
              </div>
            </header>
            <div className="trend-chart-scroll social-reason-daily-scroll"><DailyChart analysis={analysis} ref={dailySvgRef} /></div>
          </section>

          <section className="trend-table-card social-reason-daily-table-card">
            <header>
              <div><p>Tabella 2</p><h2>Andamento giornaliero N. case medi</h2><span>Dal primo all’ultimo giorno del mese, senza paginazione.</span></div>
              <button type="button" className="button button-primary" onClick={exportExcel}><Download size={15} /> Esporta Excel</button>
            </header>
            <div className="trend-table-scroll">
              <table className="trend-table social-reason-daily-table">
                <thead><tr><th>Giorno della settimana</th><th>Data</th><th>In lavorazione</th><th>Backlog</th><th>Totale lavorate</th><th>Chiuse</th></tr></thead>
                <tbody>
                  {analysis.dailyRows.map((row) => (
                    <tr key={row.date}><th scope="row">{row.weekday}</th><td>{row.day}</td><td>{formatNumber(row.working)}</td><td>{formatNumber(row.backlog)}</td><td>{formatNumber(row.totalWorked)}</td><td>{formatNumber(row.closed)}</td></tr>
                  ))}
                </tbody>
                <tfoot><tr><th scope="row">Media giornaliera</th><td /><td>{formatNumber(analysis.dailyAverages.working, 2)}</td><td>{formatNumber(analysis.dailyAverages.backlog, 2)}</td><td>{formatNumber(analysis.dailyAverages.totalWorked, 2)}</td><td>{formatNumber(analysis.dailyAverages.closed, 2)}</td></tr></tfoot>
              </table>
            </div>
            <footer><Table2 size={14} /> Valori numerici, intestazione bloccata e filtri disponibili nell’Excel.</footer>
          </section>

          {exportError && <div className="trend-export-error" role="alert"><AlertTriangle size={15} /> {exportError}</div>}
        </>
      )}
    </>
  );
}
